import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Paper, Typography, TextField, Grid, Button, Chip, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Checkbox, useMediaQuery, Collapse,
  ToggleButtonGroup, ToggleButton, Autocomplete,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import api from "../services/api";
import { openAuthPdf } from "../services/openAuthPdf";
import { todayISO } from "../utils/date";

// Largest leftover the "mark Paid" write-off will forgive (matches backend).
const WRITEOFF_MAX = 5.0;

/**
 * Shared component for "Pending Purchase Invoices" and "Pending Sale Invoices".
 * Only the invoiceType + button labels differ between the two screens.
 *
 * Props:
 *   invoiceType:  'Purchase' | 'Sale'
 */
export default function PendingInvoices({ invoiceType, onOpenInvoice }) {
  const isSale = invoiceType === "Sale";
  const partyLabel  = isSale ? "Customer" : "Supplier";
  const payLabel    = isSale ? "Amount Received" : "Pay";
  const dialogTitle = isSale ? "Record amount received" : "Pay invoice";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const today        = todayISO();
  const firstOfMonth = today.slice(0, 7) + "-01";

  const [viewMode, setViewMode] = useState("invoice"); // 'invoice' | 'account'
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);   // invoice currently being paid
  const [selected, setSelected] = useState(() => new Set()); // Master_IDs for bulk pay
  const [bulkOpen, setBulkOpen] = useState(false);
  const [needsPricing, setNeedsPricing] = useState([]); // invoices with items but no prices

  const toggleSel = (id) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const fetchRows = useCallback(() => {
    setLoading(true);
    api.get("/invoicePayments/pending", {
      params: { type: invoiceType, name: search, fromDate, toDate },
    })
      .then((r) => { setRows(Array.isArray(r.data) ? r.data : []); setSelected(new Set()); })
      .catch((e) => {
        console.error("Failed to load pending invoices:", e);
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [invoiceType, search, fromDate, toDate]);

  const selectedRows = rows.filter((r) => selected.has(r.Master_ID));
  const selectedTotal = selectedRows.reduce((s, r) => s + (Number(r.Balance) || 0), 0);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Invoices that have items but no prices yet — invisible on the pending list.
  // Deliberately NOT date-filtered: an unpriced invoice from any month should
  // always surface here so it doesn't get forgotten. Only the name search applies.
  const fetchNeedsPricing = useCallback(() => {
    api.get("/invoicePayments/needsPricing", {
      params: { type: invoiceType, name: search },
    })
      .then((r) => setNeedsPricing(Array.isArray(r.data) ? r.data : []))
      .catch(() => setNeedsPricing([]));
  }, [invoiceType, search]);
  useEffect(() => { fetchNeedsPricing(); }, [fetchNeedsPricing]);

  const totals = useMemo(() => {
    let t = 0, p = 0, b = 0;
    for (const r of rows) {
      t += Number(r.TotalAmount) || 0;
      p += Number(r.PaidAmount)  || 0;
      b += Number(r.Balance)     || 0;
    }
    return { total: t, paid: p, balance: b };
  }, [rows]);

  const statusChip = (s) => {
    const status = s || "Open";
    const color = status === "Paid" ? "success" : status === "Partial" ? "warning" : "error";
    return <Chip size="small" label={status} color={color} />;
  };

  return (
    <Box p={2}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        {isSale ? "Pending Sale Invoices" : "Pending Purchase Invoices"}
      </Typography>

      <ToggleButtonGroup
        size="small" exclusive color="primary" value={viewMode}
        onChange={(e, v) => v && setViewMode(v)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="invoice">By invoice</ToggleButton>
        <ToggleButton value="account">By account</ToggleButton>
      </ToggleButtonGroup>

      {viewMode === "account" ? (
        <AccountView
          invoiceType={invoiceType}
          isMobile={isMobile}
          onOpenInvoice={onOpenInvoice}
        />
      ) : (
      <>
      {needsPricing.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, border: 2, borderColor: "warning.main" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
            <Chip color="warning" label={`${needsPricing.length} need pricing`} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {isSale ? "Sale" : "Purchase"} invoices with items but no prices yet
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            These have a $0 total, so they don't appear in the pending list below.{" "}
            <b>Click a row</b> to open it in the {isSale ? "Sale" : "Purchase"} Invoice screen, add the rates, and save —
            it'll then show as pending. <i>Shows all dates — not affected by the date filter.</i>
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>{partyLabel}</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Items</TableCell>
                  <TableCell align="right">Net Wt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {needsPricing.map((r) => (
                  <TableRow key={r.Master_ID} hover
                    onClick={() => onOpenInvoice && onOpenInvoice(r.Master_ID)}
                    sx={{ cursor: onOpenInvoice ? "pointer" : "default" }}>
                    <TableCell sx={{ color: "primary.main", fontWeight: 600 }}>{r.Master_ID}</TableCell>
                    <TableCell>{r.Name}</TableCell>
                    <TableCell>{r.Date ? String(r.Date).slice(0, 10) : "-"}</TableCell>
                    <TableCell align="right">{r.Items}</TableCell>
                    <TableCell align="right">{(Number(r.NetWt) || 0).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField label={partyLabel} fullWidth
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 3 }}>
            <TextField type="date" label="From" InputLabelProps={{ shrink: true }} fullWidth
              value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 3 }}>
            <TextField type="date" label="To" InputLabelProps={{ shrink: true }} fullWidth
              value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 2 }}>
            <Button variant="contained" fullWidth onClick={fetchRows}>Search</Button>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2, alignItems: "center" }}>
          <Chip variant="outlined" label={`${rows.length} pending`} />
          <Chip variant="outlined" color="primary" label={`Total: $${totals.total.toFixed(2)}`} />
          <Chip variant="outlined" color="success" label={`Paid so far: $${totals.paid.toFixed(2)}`} />
          <Chip variant="outlined" color="error"   label={`Open Balance: $${totals.balance.toFixed(2)}`} />
          {selected.size > 0 && (
            <Button variant="outlined" sx={{ ml: "auto" }}
              onClick={() => openAuthPdf(`pdfReports/supplierStatement?type=${invoiceType}&ids=${encodeURIComponent([...selected].join(","))}`)}>
              🖨 Print Statement ({selected.size})
            </Button>
          )}
          {selected.size > 0 && (
            <Button variant="contained" color={isSale ? "success" : "primary"}
              onClick={() => setBulkOpen(true)}>
              {isSale ? "Receive" : "Pay"} {selected.size} selected — ${selectedTotal.toFixed(2)}
            </Button>
          )}
        </Box>
      </Paper>

      {isMobile ? (
        /* ---- Mobile: stacked cards ---- */
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {loading ? (
            <Typography align="center" sx={{ color: "text.secondary", py: 3 }}>Loading…</Typography>
          ) : rows.length === 0 ? (
            <Typography align="center" sx={{ color: "text.secondary", py: 3 }}>🎉 No pending invoices in this date range.</Typography>
          ) : rows.map((r) => (
            <Paper key={r.Master_ID} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Checkbox size="small" sx={{ p: 0.25 }} checked={selected.has(r.Master_ID)} onChange={() => toggleSel(r.Master_ID)} />
                  <Typography sx={{ fontWeight: 700 }}>#{r.Master_ID}</Typography>
                </Box>
                {statusChip(r.PaymentStatus)}
              </Box>
              <Typography variant="body2" sx={{ mb: 1 }}>{r.Name}</Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary", fontSize: 13 }}>
                <span>{r.Date ? String(r.Date).split("T")[0] : "-"}</span>
                <span>Total ${(Number(r.TotalAmount) || 0).toFixed(2)}</span>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>Paid ${(Number(r.PaidAmount) || 0).toFixed(2)}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "error.main" }}>Balance ${(Number(r.Balance) || 0).toFixed(2)}</Typography>
              </Box>
              <Button fullWidth size="small" variant="contained" color={isSale ? "success" : "primary"} sx={{ mt: 1.5 }} onClick={() => setEditing(r)}>
                {payLabel}
              </Button>
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox size="small"
                    checked={rows.length > 0 && selected.size === rows.length}
                    indeterminate={selected.size > 0 && selected.size < rows.length}
                    onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.Master_ID)) : new Set())} />
                </TableCell>
                <TableCell>Invoice #</TableCell>
                <TableCell>{partyLabel}</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ color: "text.secondary" }}>Loading…</TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ color: "text.secondary" }}>
                    🎉 No pending invoices in this date range.
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.Master_ID} hover selected={selected.has(r.Master_ID)}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={selected.has(r.Master_ID)} onChange={() => toggleSel(r.Master_ID)} />
                  </TableCell>
                  <TableCell>{r.Master_ID}</TableCell>
                  <TableCell>{r.Name}</TableCell>
                  <TableCell>{r.Date ? String(r.Date).split("T")[0] : "-"}</TableCell>
                  <TableCell align="right">${(Number(r.TotalAmount) || 0).toFixed(2)}</TableCell>
                  <TableCell align="right">${(Number(r.PaidAmount)  || 0).toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "error.main" }}>
                    ${(Number(r.Balance) || 0).toFixed(2)}
                  </TableCell>
                  <TableCell>{statusChip(r.PaymentStatus)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="contained"
                      color={isSale ? "success" : "primary"}
                      onClick={() => setEditing(r)}>
                      {payLabel}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PayDialog
        open={!!editing}
        invoice={editing}
        invoiceType={invoiceType}
        title={dialogTitle}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchRows(); }}
      />

      <BulkPayDialog
        open={bulkOpen}
        invoices={selectedRows}
        invoiceType={invoiceType}
        total={selectedTotal}
        onClose={() => setBulkOpen(false)}
        onSaved={() => { setBulkOpen(false); fetchRows(); }}
      />
      </>
      )}
    </Box>
  );
}

/* ================= Bulk / split payment dialog =================
 * Pays the selected invoices, optionally splitting across multiple methods
 * (e.g. $10,000 Cash + remainder Check). One Cash line for the full total
 * reproduces the old "pay all" behaviour. */
function BulkPayDialog({ open, invoices, invoiceType, total, onClose, onSaved }) {
  const isSale = invoiceType === "Sale";
  const today = todayISO();
  const balanceTotal = Number(total) || 0;
  const [splits, setSplits] = useState([{ method: "Cash", amount: "", checkNumber: "" }]);
  const [paymentDate, setPaymentDate] = useState(today);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSplits([{ method: "Cash", amount: balanceTotal.toFixed(2), checkNumber: "" }]);
      setPaymentDate(today);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const entered = splits.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const diff = Number((balanceTotal - entered).toFixed(2));

  const setLine = (i, patch) => setSplits((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setSplits((prev) => [
    ...prev,
    { method: prev.some((l) => l.method === "Cash") ? "Check" : "Cash", amount: diff > 0.005 ? diff.toFixed(2) : "", checkNumber: "" },
  ]);
  const removeLine = (i) => setSplits((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));

  const save = async () => {
    for (const l of splits) {
      if (!(Number(l.amount) > 0)) { alert("Each payment line needs an amount greater than 0."); return; }
      if (l.method === "Check" && !String(l.checkNumber).trim()) { alert("Check # is required for check lines."); return; }
    }
    if (entered > balanceTotal + 0.005) {
      alert(`Entered $${entered.toFixed(2)} is more than the balance $${balanceTotal.toFixed(2)}.`); return;
    }
    if (entered < balanceTotal - 0.005) {
      if (!window.confirm(`You entered $${entered.toFixed(2)} of $${balanceTotal.toFixed(2)}. The remaining $${diff.toFixed(2)} stays open. Continue?`)) return;
    }
    try {
      setSaving(true);
      await api.post("/invoicePayments/split", {
        invoiceType,
        invoiceIds: invoices.map((r) => r.Master_ID),
        splits: splits.map((l) => ({
          method: l.method,
          amount: Number(l.amount),
          checkNumber: l.method === "Cash" ? null : (String(l.checkNumber).trim() || null),
        })),
        paymentDate,
      });
      onSaved();
    } catch (err) {
      alert("Payment failed: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isSale ? "Receive against" : "Pay"} {invoices.length} invoice{invoices.length === 1 ? "" : "s"}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ maxHeight: 130, overflowY: "auto", mb: 1 }}>
          {invoices.map((r) => (
            <Box key={r.Master_ID} sx={{ display: "flex", justifyContent: "space-between", py: 0.5, borderBottom: "1px solid #eef2f6" }}>
              <span>#{r.Master_ID} · {r.Name}</span>
              <b>${(Number(r.Balance) || 0).toFixed(2)}</b>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography sx={{ fontWeight: 700 }}>Total balance</Typography>
          <Typography sx={{ fontWeight: 800 }}>${balanceTotal.toFixed(2)}</Typography>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Payment — split across methods if needed</Typography>
        {splits.map((l, i) => (
          <Grid container spacing={1} key={i} sx={{ mb: 1 }} alignItems="center">
            <Grid size={{ xs: 4 }}>
              <TextField select fullWidth size="small" label="Method" value={l.method} onChange={(e) => setLine(i, { method: e.target.value })}>
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Check">Check</MenuItem>
                <MenuItem value="ACH">ACH</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <TextField fullWidth size="small" type="number" label="Amount" value={l.amount} onChange={(e) => setLine(i, { amount: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 3 }}>
              {(l.method === "Check" || l.method === "ACH") && (
                <TextField fullWidth size="small" label={l.method === "ACH" ? "Ref # (opt)" : "Check #"}
                  value={l.checkNumber} onChange={(e) => setLine(i, { checkNumber: e.target.value })} />
              )}
            </Grid>
            <Grid size={{ xs: 2 }}>
              {splits.length > 1 && <Button size="small" color="error" onClick={() => removeLine(i)}>✕</Button>}
            </Grid>
          </Grid>
        ))}
        <Button size="small" onClick={addLine}>+ Add split line</Button>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          <Typography variant="body2">Entered: <b>${entered.toFixed(2)}</b></Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: Math.abs(diff) <= 0.005 ? "success.main" : diff > 0 ? "warning.main" : "error.main" }}>
            {Math.abs(diff) <= 0.005 ? "✓ matches balance" : diff > 0 ? `$${diff.toFixed(2)} remaining` : `$${(-diff).toFixed(2)} over`}
          </Typography>
        </Box>
        <Box sx={{ mt: 1.5, maxWidth: 220 }}>
          <TextField type="date" fullWidth size="small" label="Date" InputLabelProps={{ shrink: true }}
            value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving} onClick={save}>
          {saving ? "Saving…" : `${isSale ? "Receive" : "Pay"} $${entered.toFixed(2)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ================= Payment dialog ================= */
function PayDialog({ open, invoice, invoiceType, title, onClose, onSaved }) {
  const isSale = invoiceType === "Sale";
  const today = todayISO();

  const [method, setMethod] = useState("Cash");
  const [amount, setAmount] = useState("");
  const [amountTouched, setAmountTouched] = useState(false);
  const [checkNumber, setCheckNumber] = useState("");
  const [paymentDate, setPaymentDate] = useState(today);
  const [saving, setSaving] = useState(false);
  const [writeOff, setWriteOff] = useState(false);

  useEffect(() => {
    if (open && invoice) {
      setMethod("Cash");
      setAmount((Number(invoice.Balance) || 0).toFixed(2));
      setAmountTouched(false);
      setCheckNumber("");
      setPaymentDate(today);
      setWriteOff(false);
    }
  }, [open, invoice, today]);

  if (!open || !invoice) return null;

  const balance = Number(invoice.Balance) || 0;
  const amtNum  = Number(amount) || 0;
  const shortfall = Number((balance - amtNum).toFixed(2));
  // Show the "mark Paid" option only for a small leftover (cents/rounding).
  const canWriteOff = amtNum > 0 && shortfall > 0.005 && shortfall <= WRITEOFF_MAX;
  const effectiveWriteOff = canWriteOff && writeOff;

  const save = async () => {
    if (amtNum <= 0) { alert("Amount must be greater than zero."); return; }
    if (method === "Check" && !checkNumber.trim()) {
      alert("Check # is required for check payments."); return;
    }
    if (amtNum > balance + 0.005) {
      const ok = window.confirm(
        `You're recording $${amtNum.toFixed(2)} against a balance of $${balance.toFixed(2)}. Continue?`
      );
      if (!ok) return;
    }
    try {
      setSaving(true);
      await api.post("/invoicePayments", {
        invoiceId:   invoice.Master_ID,
        invoiceType,
        amount:      amtNum,
        method,
        checkNumber: (method === "Check" || method === "ACH") ? (checkNumber.trim() || null) : null,
        paymentDate,
        partyName:   invoice.Name,
        writeOffBalance: effectiveWriteOff,
      });
      onSaved();
    } catch (err) {
      alert("Payment failed: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>Invoice</Typography>
          <Typography sx={{ fontWeight: 700 }}>
            #{invoice.Master_ID} · {invoice.Name}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
            <Chip size="small" variant="outlined" label={`Total $${(Number(invoice.TotalAmount) || 0).toFixed(2)}`} />
            <Chip size="small" variant="outlined" color="success" label={`Paid $${(Number(invoice.PaidAmount) || 0).toFixed(2)}`} />
            <Chip size="small" variant="outlined" color="error"   label={`Balance $${balance.toFixed(2)}`} />
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField select fullWidth label="Method"
              value={method} onChange={(e) => setMethod(e.target.value)}>
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="Check">Check</MenuItem>
              <MenuItem value="ACH">ACH (bank transfer)</MenuItem>
            </TextField>
          </Grid>
          {(method === "Check" || method === "ACH") && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label={method === "ACH" ? "ACH Ref # (optional)" : "Check #"}
                value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField type="number" fullWidth label="Amount"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setAmountTouched(true); }}
              inputProps={{ step: "0.01", min: "0" }}
              helperText={
                !amountTouched ? "Defaults to current balance"
                  : effectiveWriteOff ? `Invoice will be marked Paid (${shortfall.toFixed(2)} written off)`
                  : amtNum < balance - 0.005 ? "Partial payment — invoice will stay Partial"
                  : amtNum > balance + 0.005 ? "Overpayment"
                  : "Will mark invoice as Paid"
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField type="date" fullWidth label="Date"
              InputLabelProps={{ shrink: true }}
              value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
          </Grid>
          {canWriteOff && (
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={<Checkbox checked={writeOff} onChange={(e) => setWriteOff(e.target.checked)} />}
                label={`Mark invoice fully Paid — write off remaining $${shortfall.toFixed(2)} (rounding)`}
              />
            </Grid>
          )}
        </Grid>

        <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={
              isSale
                ? `Will post a $${amtNum.toFixed(2)} ${method === "Check" ? "check" : "cash"} entry to Daily Cash Received on ${paymentDate}`
                : `Will post a $${amtNum.toFixed(2)} ${method === "Check" ? "check" : "cash"} entry to Daily Cash Payment on ${paymentDate}`
            }
          />
          {effectiveWriteOff && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={`$${shortfall.toFixed(2)} written off — not added to the register`}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Record Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ================= Account-level (FIFO) view =================
 * One row per account that has pending invoices or leftover advance credit.
 * Receiving/paying money applies it to the account's oldest invoices first; any
 * leftover is held as advance credit and auto-absorbs the next invoice. */
function AccountView({ invoiceType, isMobile, onOpenInvoice }) {
  const isSale = invoiceType === "Sale";
  const partyLabel = isSale ? "Customer" : "Supplier";
  const payLabel   = isSale ? "Receive" : "Pay";

  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [invByAcct, setInvByAcct] = useState({}); // accountName -> invoices[]
  const [paying, setPaying] = useState(null);      // account row in the pay dialog
  const [contra, setContra] = useState(null);      // account row in the contra dialog
  const [syncing, setSyncing] = useState(null);    // accountName currently syncing to GL
  const [advanceOpen, setAdvanceOpen] = useState(false); // "Record advance" dialog

  const fetchAccounts = useCallback(() => {
    setLoading(true);
    api.get("/invoicePayments/pendingByAccount", { params: { type: invoiceType, name: search } })
      .then((r) => setAccounts(Array.isArray(r.data) ? r.data : []))
      .catch((e) => { console.error("Failed to load accounts:", e); setAccounts([]); })
      .finally(() => setLoading(false));
  }, [invoiceType, search]);
  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const loadInvoices = useCallback((name) => {
    api.get("/invoicePayments/pending", { params: { type: invoiceType, name } })
      .then((r) => {
        const list = (Array.isArray(r.data) ? r.data : [])
          .filter((x) => (x.Name || "").trim().toUpperCase() === (name || "").trim().toUpperCase());
        setInvByAcct((prev) => ({ ...prev, [name]: list }));
      })
      .catch(() => setInvByAcct((prev) => ({ ...prev, [name]: [] })));
  }, [invoiceType]);

  const toggleExpand = (name) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(name)) { n.delete(name); }
      else { n.add(name); if (!invByAcct[name]) loadInvoices(name); }
      return n;
    });
  };

  const totals = accounts.reduce(
    (a, r) => ({
      owed: a.owed + (Number(r.balanceOwed) || 0),
      credit: a.credit + (Number(r.credit) || 0),
      net: a.net + (Number(r.netOwed) || 0),
    }),
    { owed: 0, credit: 0, net: 0 }
  );

  const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
  const [sweeping, setSweeping] = useState(false);

  // "Sync to GL": clear the amount by which Pending exceeds the General Ledger
  // (a real payment already in the ledger that was never applied to the
  // invoices) with non-cash Adjustment rows. The ledger itself is not changed.
  const handleSync = (a) => {
    const amt = Number(a.syncable) || 0;
    if (!(amt > 0.005)) return;
    const ok = window.confirm(
      `Sync ${a.accountName} to the General Ledger?\n\n` +
      `Pending Net owed: ${money(a.netOwed)}\n` +
      `General Ledger:   ${money(a.glOwed)}\n\n` +
      `This clears ${money(amt)} off the oldest open invoice(s) with a non-cash ` +
      `adjustment so Pending matches the ledger. The General Ledger is NOT changed.`
    );
    if (!ok) return;
    setSyncing(a.accountName);
    api.post("/invoicePayments/syncToGl", { accountName: a.accountName, invoiceType })
      .then((r) => {
        window.alert(r.data?.message || "Synced.");
        setInvByAcct((prev) => { const n = { ...prev }; delete n[a.accountName]; return n; });
        fetchAccounts();
      })
      .catch((e) => window.alert(e?.response?.data?.error || "Sync failed."))
      .finally(() => setSyncing(null));
  };

  /* Sweep up the pennies.
   *
   * A yard does not chase 38 cents, but Pending shows it as money owed and a
   * supplier with 35c against his name reads as unpaid at a glance. A payment
   * already forgives a sub-$5 leftover on its own; these are the ones that
   * never went through that path. Each is closed with a non-cash adjustment
   * AND cleared from the party's ledger, so both screens stay level and no
   * cash moves - because none did. */
  const handleWriteOffSmall = () => {
    setSweeping(true);
    api.post("/invoicePayments/writeOffSmall", { invoiceType, maxAmount: 5, dryRun: true })
      .then((r) => {
        const d = r.data || {};
        if (!d.count) { window.alert("Nothing to write off — no invoice has under $5 left."); return null; }
        const lines = (d.invoices || []).slice(0, 12)
          .map((x) => `   ${x.account} — ${x.invoiceId} — ${money(x.amount)}`).join("\n");
        const more = d.count > 12 ? `\n   …and ${d.count - 12} more` : "";
        const ok = window.confirm(
          `Write off ${d.count} leftover${d.count === 1 ? "" : "s"} of under $5 — ` +
          `${money(d.total)} in total?\n\n${lines}${more}\n\n` +
          `Each invoice is closed and the same amount is cleared from that ` +
          `party's ledger. No cash moves.`);
        if (!ok) return null;
        return api.post("/invoicePayments/writeOffSmall", { invoiceType, maxAmount: 5 });
      })
      .then((r) => {
        if (!r) return;
        window.alert(r.data?.message || "Done.");
        setInvByAcct({});
        fetchAccounts();
      })
      .catch((e) => window.alert(e?.response?.data?.error || "Could not write them off."))
      .finally(() => setSweeping(false));
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 8, md: 6 }}>
            <TextField label={partyLabel} fullWidth value={search}
              onChange={(e) => setSearch(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <Button variant="contained" fullWidth onClick={fetchAccounts}>Search</Button>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Button variant="outlined" color={isSale ? "success" : "primary"} fullWidth
              onClick={() => setAdvanceOpen(true)}
              title={`Record money ${isSale ? "received from a customer" : "paid to a supplier"} who has no pending invoice`}>
              Record advance
            </Button>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Button variant="outlined" color="warning" fullWidth
              disabled={sweeping}
              onClick={handleWriteOffSmall}
              title="Close every invoice with under $5 still on it, and clear the same amount from the ledger">
              {sweeping ? "Checking…" : "Write off under $5"}
            </Button>
          </Grid>
        </Grid>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
          <Chip variant="outlined" label={`${accounts.length} accounts`} />
          <Chip variant="outlined" color="error" label={`Owed: ${money(totals.owed)}`} />
          <Chip variant="outlined" color="success" label={`Advance credit: ${money(totals.credit)}`} />
          <Chip variant="outlined" color="primary" label={`Net: ${money(totals.net)}`} />
        </Box>
      </Paper>

      {loading ? (
        <Typography align="center" sx={{ color: "text.secondary", py: 3 }}>Loading…</Typography>
      ) : accounts.length === 0 ? (
        <Typography align="center" sx={{ color: "text.secondary", py: 3 }}>🎉 No accounts with a pending balance.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>{partyLabel}</TableCell>
                <TableCell align="right">Pending</TableCell>
                <TableCell align="right">Billed</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Balance owed</TableCell>
                <TableCell align="right">Advance credit</TableCell>
                <TableCell align="right">Net owed</TableCell>
                <TableCell align="right">GL balance</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((a) => {
                const open = expanded.has(a.accountName);
                const invs = invByAcct[a.accountName] || [];
                return (
                  <React.Fragment key={a.accountName}>
                    <TableRow hover>
                      <TableCell>
                        <Button size="small" sx={{ minWidth: 32 }} onClick={() => toggleExpand(a.accountName)}>
                          {open ? "▾" : "▸"}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{a.accountName}</TableCell>
                      <TableCell align="right">{a.pendingCount}</TableCell>
                      <TableCell align="right">{money(a.totalBilled)}</TableCell>
                      <TableCell align="right">{money(a.totalPaid)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: "error.main" }}>{money(a.balanceOwed)}</TableCell>
                      <TableCell align="right" sx={{ color: a.credit > 0.005 ? "success.main" : "text.secondary" }}>
                        {money(a.credit)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{money(a.netOwed)}</TableCell>
                      <TableCell align="right"
                        title={a.syncable > 0.005
                          ? `Pending is ${money(a.syncable)} higher than the General Ledger`
                          : "Matches the General Ledger"}
                        sx={{ color: a.syncable > 0.005 ? "warning.main" : "text.secondary",
                              fontWeight: a.syncable > 0.005 ? 700 : 400 }}>
                        {money(a.glOwed)}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "inline-flex", gap: 1 }}>
                          {a.syncable > 0.005 && (
                            <Button size="small" variant="outlined" color="warning"
                              disabled={syncing === a.accountName}
                              onClick={() => handleSync(a)}
                              title={`Clear the ${money(a.syncable)} gap so Pending matches the General Ledger`}>
                              {syncing === a.accountName ? "Syncing…" : "Sync to GL"}
                            </Button>
                          )}
                          <Button size="small" variant="outlined" color="secondary"
                            onClick={() => setContra(a)} title="Net a sale against a purchase for this account">
                            Contra
                          </Button>
                          <Button size="small" variant="contained" color={isSale ? "success" : "primary"}
                            onClick={() => setPaying(a)}>
                            {payLabel}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={10} sx={{ py: 0, border: 0 }}>
                        <Collapse in={open} unmountOnExit>
                          <Box sx={{ py: 1, pl: 4 }}>
                            {invs.length === 0 ? (
                              <Typography variant="body2" sx={{ color: "text.secondary" }}>No open invoices.</Typography>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Invoice #</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="right">Paid</TableCell>
                                    <TableCell align="right">Balance</TableCell>
                                    <TableCell>Status</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {invs.map((r) => (
                                    <TableRow key={r.Master_ID} hover
                                      onClick={() => onOpenInvoice && onOpenInvoice(r.Master_ID)}
                                      sx={{ cursor: onOpenInvoice ? "pointer" : "default" }}>
                                      <TableCell sx={{ color: "primary.main" }}>{r.Master_ID}</TableCell>
                                      <TableCell>{r.Date ? String(r.Date).split("T")[0] : "-"}</TableCell>
                                      <TableCell align="right">{money(r.TotalAmount)}</TableCell>
                                      <TableCell align="right">{money(r.PaidAmount)}</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 600, color: "error.main" }}>{money(r.Balance)}</TableCell>
                                      <TableCell>
                                        <Chip size="small"
                                          color={r.PaymentStatus === "Partial" ? "warning" : "error"}
                                          label={r.PaymentStatus || "Open"} />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AccountPayDialog
        open={!!paying}
        account={paying}
        invoiceType={invoiceType}
        onClose={() => setPaying(null)}
        onSaved={(name) => {
          setPaying(null);
          fetchAccounts();
          if (name) loadInvoices(name);
        }}
      />

      <ContraDialog
        open={!!contra}
        account={contra}
        onClose={() => setContra(null)}
        onSaved={(name) => {
          setContra(null);
          fetchAccounts();
          if (name) loadInvoices(name);
        }}
      />

      <RecordAdvanceDialog
        open={advanceOpen}
        invoiceType={invoiceType}
        onClose={() => setAdvanceOpen(false)}
        onSaved={(name) => {
          setAdvanceOpen(false);
          fetchAccounts();
          if (name) loadInvoices(name);
        }}
      />
    </Box>
  );
}

/* ================= Contra dialog (net sale ↔ purchase) =================
 * For an account that is both a customer AND a vendor. Offsets equal amounts off
 * its pending Sale and Purchase invoices with non-cash Adjustments — the General
 * Ledger already nets both sides by name, so no money and no ledger change. */
function ContraDialog({ open, account, onClose, onSaved }) {
  const today = todayISO();
  const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // { sale, purchase, maxContra }
  const [amount, setAmount] = useState("");
  const [contraDate, setContraDate] = useState(today);
  const [saving, setSaving] = useState(false);

  const accountName = account?.accountName;

  useEffect(() => {
    if (!open || !accountName) return;
    setLoading(true);
    setPreview(null);
    setContraDate(today);
    api.get("/invoicePayments/contraPreview", { params: { accountName } })
      .then((r) => {
        setPreview(r.data);
        setAmount(Number(r.data?.maxContra || 0).toFixed(2));
      })
      .catch((e) => alert("Failed to load contra preview: " + (e.response?.data?.error || e.message)))
      .finally(() => setLoading(false));
  }, [open, accountName]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !account) return null;

  const maxContra = Number(preview?.maxContra || 0);
  const amtNum = Number(amount) || 0;
  const overMax = amtNum > maxContra + 0.005;

  const save = async () => {
    if (amtNum <= 0) { alert("Amount must be greater than zero."); return; }
    if (overMax) { alert(`Amount can't exceed the max offset ${money(maxContra)}.`); return; }
    try {
      setSaving(true);
      const res = await api.post("/invoicePayments/contra", {
        accountName, amount: amtNum, paymentDate: contraDate,
      });
      const d = res.data || {};
      alert(`Contra ${money(d.contraAmount)} recorded — cleared ${d.sale?.count || 0} sale and ` +
            `${d.purchase?.count || 0} purchase invoice line(s). General Ledger unchanged.`);
      onSaved(accountName);
    } catch (err) {
      alert("Contra failed: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Contra — {accountName}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Typography sx={{ color: "text.secondary", py: 2 }}>Loading…</Typography>
        ) : !preview ? (
          <Typography sx={{ color: "text.secondary", py: 2 }}>No data.</Typography>
        ) : maxContra <= 0.005 ? (
          <Box sx={{ py: 1 }}>
            <Typography sx={{ mb: 1 }}>Nothing to offset.</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              A contra needs BOTH pending sale and pending purchase invoices for this account.
              Pending sale: <b>{money(preview.sale.total)}</b> · pending purchase: <b>{money(preview.purchase.total)}</b>.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              <Chip size="small" variant="outlined" color="success" label={`Pending sale ${money(preview.sale.total)}`} />
              <Chip size="small" variant="outlined" color="primary" label={`Pending purchase ${money(preview.purchase.total)}`} />
              <Chip size="small" color="secondary" label={`Max offset ${money(maxContra)}`} />
            </Box>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Clears the same amount off both sides (oldest-first) with non-cash adjustments.
              <b> No money moves and the General Ledger is unchanged</b> — it already nets both
              sides by name. Only the offsetting invoices drop off the Pending lists.
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField type="number" fullWidth label="Contra amount" value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  inputProps={{ step: "0.01", min: "0", max: maxContra }}
                  error={overMax}
                  helperText={overMax ? `Max is ${money(maxContra)}` : `Up to ${money(maxContra)}`} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField type="date" fullWidth label="Date" InputLabelProps={{ shrink: true }}
                  value={contraDate} onChange={(e) => setContraDate(e.target.value)} />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {preview && maxContra > 0.005 && (
          <Button variant="contained" color="secondary" disabled={saving || overMax || amtNum <= 0} onClick={save}>
            {saving ? "Saving…" : `Contra ${money(amtNum)}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/* ================= Account FIFO payment dialog ================= */
function AccountPayDialog({ open, account, invoiceType, onClose, onSaved }) {
  const isSale = invoiceType === "Sale";
  const today = todayISO();

  const [method, setMethod] = useState("Cash");
  const [amount, setAmount] = useState("");
  const [checkNumber, setCheckNumber] = useState("");
  const [paymentDate, setPaymentDate] = useState(today);
  const [saving, setSaving] = useState(false);

  const owed = Number(account?.balanceOwed) || 0;

  useEffect(() => {
    if (open && account) {
      setMethod("Cash");
      setAmount(owed.toFixed(2));
      setCheckNumber("");
      setPaymentDate(today);
    }
  }, [open, account]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !account) return null;

  const amtNum = Number(amount) || 0;
  const advance = Number((amtNum - owed).toFixed(2));

  const save = async () => {
    if (amtNum <= 0) { alert("Amount must be greater than zero."); return; }
    if (method === "Check" && !checkNumber.trim()) { alert("Check # is required for check payments."); return; }
    if (advance > 0.005) {
      const ok = window.confirm(
        `${money(amtNum)} is ${money(advance)} more than the ${money(owed)} owed. ` +
        `The extra will be held as advance credit for the next invoice. Continue?`
      );
      if (!ok) return;
    }
    try {
      setSaving(true);
      const res = await api.post("/invoicePayments/payAccount", {
        invoiceType,
        accountName: account.accountName,
        amount: amtNum,
        method,
        checkNumber: (method === "Check" || method === "ACH") ? (checkNumber.trim() || null) : null,
        paymentDate,
      });
      const d = res.data || {};
      let msg = `Applied ${money(d.totalApplied)} to ${d.invoicesPaid} invoice(s).`;
      if (d.advanceCredit) msg += ` ${money(d.advanceCredit.amount)} held as advance credit.`;
      alert(msg);
      onSaved(account.accountName);
    } catch (err) {
      alert("Payment failed: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isSale ? "Receive from" : "Pay"} {account.accountName}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          <Chip size="small" variant="outlined" color="error" label={`Balance owed ${money(owed)}`} />
          {account.credit > 0.005 && (
            <Chip size="small" variant="outlined" color="success" label={`Advance credit ${money(account.credit)}`} />
          )}
          <Chip size="small" variant="outlined" label={`${account.pendingCount} pending invoice(s)`} />
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Money is applied to the <b>oldest invoices first</b>. Any amount beyond what's owed
          is held as <b>advance credit</b> and auto-applies to the next invoice.
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField select fullWidth label="Method" value={method} onChange={(e) => setMethod(e.target.value)}>
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="Check">Check</MenuItem>
              <MenuItem value="ACH">ACH (bank transfer)</MenuItem>
            </TextField>
          </Grid>
          {(method === "Check" || method === "ACH") && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label={method === "ACH" ? "ACH Ref # (optional)" : "Check #"}
                value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField type="number" fullWidth label="Amount" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ step: "0.01", min: "0" }}
              helperText={advance > 0.005 ? `${money(advance)} will be advance credit` : "Applied oldest-first"} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField type="date" fullWidth label="Date" InputLabelProps={{ shrink: true }}
              value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving} onClick={save}>
          {saving ? "Saving…" : `${isSale ? "Receive" : "Pay"} ${money(amtNum)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ================= Record advance (account may have NO pending invoice) =========
 * The account rows above only exist for parties that have a pending invoice or
 * leftover credit, so there is no button to take money from a customer who has
 * nothing open yet. This dialog lets any account be picked by name and posts to
 * the SAME /payAccount endpoint: whatever is owed is settled oldest-first, and
 * everything past that is held as advance credit for their next invoice.
 */
function RecordAdvanceDialog({ open, invoiceType, onClose, onSaved }) {
  const isSale = invoiceType === "Sale";
  const partyLabel = isSale ? "Customer" : "Supplier";
  const today = todayISO();
  const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

  const [names, setNames] = useState([]);
  const [accountName, setAccountName] = useState("");
  const [summary, setSummary] = useState(null);   // { balanceOwed, credit, pendingCount } | null
  const [method, setMethod] = useState("ACH");    // advances are usually wired
  const [amount, setAmount] = useState("");
  const [checkNumber, setCheckNumber] = useState("");
  const [paymentDate, setPaymentDate] = useState(today);
  const [saving, setSaving] = useState(false);

  // Full account list (same source as the invoice screens) so the name recorded
  // matches the name on their invoices exactly.
  useEffect(() => {
    if (!open) return;
    setAccountName(""); setSummary(null); setMethod("ACH");
    setAmount(""); setCheckNumber(""); setPaymentDate(today);
    api.get("/accounts")
      .then((r) => setNames(
        (Array.isArray(r.data) ? r.data : [])
          .map((a) => (a.SubAccount_Name || "").trim())
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b))
      ))
      .catch(() => setNames([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // What does this account already have open? Drives the preview text below.
  useEffect(() => {
    const nm = (accountName || "").trim();
    if (!open || !nm) { setSummary(null); return; }
    let cancelled = false;
    api.get("/invoicePayments/pendingByAccount", { params: { type: invoiceType, name: nm } })
      .then((r) => {
        if (cancelled) return;
        const row = (Array.isArray(r.data) ? r.data : [])
          .find((x) => (x.accountName || "").trim().toUpperCase() === nm.toUpperCase());
        setSummary(row || { balanceOwed: 0, credit: 0, pendingCount: 0 });
      })
      .catch(() => { if (!cancelled) setSummary({ balanceOwed: 0, credit: 0, pendingCount: 0 }); });
    return () => { cancelled = true; };
  }, [open, accountName, invoiceType]);

  if (!open) return null;

  const amtNum = Number(amount) || 0;
  const owed = Number(summary?.balanceOwed) || 0;
  const toInvoices = Number(Math.min(amtNum, owed).toFixed(2));
  const toCredit = Number(Math.max(amtNum - owed, 0).toFixed(2));

  const save = async () => {
    const nm = (accountName || "").trim();
    if (!nm) { alert(`Pick a ${partyLabel.toLowerCase()}.`); return; }
    if (amtNum <= 0) { alert("Amount must be greater than zero."); return; }
    if (method === "Check" && !checkNumber.trim()) { alert("Check # is required for check payments."); return; }

    const ok = window.confirm(
      `${isSale ? "Receive" : "Pay"} ${money(amtNum)} ${isSale ? "from" : "to"} ${nm} by ${method} on ${paymentDate}.\n\n` +
      (toInvoices > 0.005
        ? `• ${money(toInvoices)} closes their oldest open invoice(s)\n`
        : `• They have no open invoices\n`) +
      `• ${money(toCredit)} held as advance credit for their next invoice\n\n` +
      `Nothing already recorded is changed. Continue?`
    );
    if (!ok) return;

    try {
      setSaving(true);
      const res = await api.post("/invoicePayments/payAccount", {
        invoiceType,
        accountName: nm,
        amount: amtNum,
        method,
        checkNumber: (method === "Check" || method === "ACH") ? (checkNumber.trim() || null) : null,
        paymentDate,
      });
      const d = res.data || {};
      let msg = `Applied ${money(d.totalApplied)} to ${d.invoicesPaid} invoice(s).`;
      if (d.advanceCredit) msg += ` ${money(d.advanceCredit.amount)} held as advance credit for ${nm}.`;
      alert(msg);
      onSaved(nm);
    } catch (err) {
      alert("Failed: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record advance {isSale ? "received" : "paid"}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Use this when {isSale ? "a customer sends money" : "you send a supplier money"} before
          there is an invoice. Anything they already owe is settled <b>oldest invoice first</b>;
          the rest is held as <b>advance credit</b> and auto-applies to their next invoice.
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              freeSolo
              options={names}
              value={accountName}
              onChange={(_e, v) => setAccountName(v || "")}
              onInputChange={(_e, v) => setAccountName(v || "")}
              renderInput={(params) => (
                <TextField {...params} label={partyLabel} autoFocus
                  helperText="Pick from the account list so the name matches their invoices" />
              )}
            />
          </Grid>

          {summary && (
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip size="small" variant="outlined"
                  color={owed > 0.005 ? "error" : "default"}
                  label={`Currently owed ${money(owed)}`} />
                {Number(summary.credit) > 0.005 && (
                  <Chip size="small" variant="outlined" color="success"
                    label={`Existing advance credit ${money(summary.credit)}`} />
                )}
                <Chip size="small" variant="outlined"
                  label={`${summary.pendingCount || 0} pending invoice(s)`} />
              </Box>
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField select fullWidth label="Method" value={method} onChange={(e) => setMethod(e.target.value)}>
              <MenuItem value="ACH">ACH (bank transfer)</MenuItem>
              <MenuItem value="Check">Check</MenuItem>
              <MenuItem value="Cash">Cash</MenuItem>
            </TextField>
          </Grid>
          {(method === "Check" || method === "ACH") && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label={method === "ACH" ? "ACH Ref # (optional)" : "Check #"}
                value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField type="number" fullWidth label="Amount" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ step: "0.01", min: "0" }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField type="date" fullWidth label="Date received" InputLabelProps={{ shrink: true }}
              value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
          </Grid>

          {amtNum > 0 && (
            <Grid size={{ xs: 12 }}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2">
                  {toInvoices > 0.005
                    ? <>Closes <b>{money(toInvoices)}</b> of open invoices, and </>
                    : <>No open invoices — </>}
                  <b>{money(toCredit)}</b> is held as advance credit.
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {method === "ACH" || (isSale && method === "Check")
                    ? "Bank money — moves the General Ledger, no cash-drawer line."
                    : "Posts a Daily Cash line so the cashier report sees it."}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color={isSale ? "success" : "primary"}
          disabled={saving || !accountName.trim() || amtNum <= 0} onClick={save}>
          {saving ? "Saving…" : `Record ${money(amtNum)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
